import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Change, Loader, Empty, Stat, DataTag, LiveBadge, LivePrice, AmountChips } from "@/components/widgets";
import useLivePrices from "@/hooks/useLivePrices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PlusCircle, ArrowClockwise, Bank, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Gate } from "@/components/Gate";
import AlpacaBroker from "@/components/AlpacaBroker";
import { useSub } from "@/context/SubscriptionContext";
import { AssetLogo } from "@/components/AssetLogo";

const COLORS = ["#007AFF", "#34C759", "#FFCC00", "#FF3B30", "#8A8A93", "#5E5CE6", "#FF9500"];

export default function Trading() {
  const nav = useNavigate();
  const { has } = useSub();
  const [pf, setPf] = useState(null);
  const [trades, setTrades] = useState([]);
  const [mode, setMode] = useState("paper");
  const [brokerStatus, setBrokerStatus] = useState(null);

  const load = () => {
    api.get("/portfolio").then((r) => setPf(r.data)).catch(() => {});
    api.get("/orders").then((r) => setTrades(r.data.trades)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (has("elite")) api.get("/alpaca/status").then((r) => setBrokerStatus(r.data)).catch(() => setBrokerStatus(null));
  }, [has]);

  const close = async (t) => {
    try { await api.post(`/positions/${t}/close`); toast.success(`Closed ${t}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const closeAll = async () => {
    if (!window.confirm(`Close ALL ${pf.positions.length} open paper position${pf.positions.length === 1 ? "" : "s"} at market? This sells everything.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/positions/close-all");
      const pnl = r.data.realized_pnl ?? 0;
      const sign = pnl >= 0 ? "+" : "-";
      toast.success(`Closed ${r.data.count} position${r.data.count === 1 ? "" : "s"}`, {
        description: `Realized P/L: ${sign}$${Math.abs(pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const { prices: live, connected } = useLivePrices(pf ? pf.positions.map((p) => p.ticker) : []);

  const [showFunds, setShowFunds] = useState(false);
  const [amount, setAmount] = useState(10000);
  const [busy, setBusy] = useState(false);

  const deposit = async (amt) => {
    const value = Number(amt);
    if (!value || value <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const r = await api.post("/portfolio/deposit", { amount: value });
      setPf(r.data);
      toast.success(`Added $${value.toLocaleString()} to your paper account`);
      setShowFunds(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resetAccount = async () => {
    if (!window.confirm("Reset your paper account to $100,000? This closes all positions and clears trade history.")) return;
    setBusy(true);
    try {
      const r = await api.post("/portfolio/reset");
      setPf(r.data); setTrades([]);
      toast.success("Account reset to $100,000");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Copy a paper position to the linked Alpaca broker
  const [copyPos, setCopyPos] = useState(null);
  const [copyQty, setCopyQty] = useState(0);
  const [copyMode, setCopyMode] = useState("qty"); // "qty" | "usd"
  const [copyUsd, setCopyUsd] = useState("1000");
  const [copyBusy, setCopyBusy] = useState(false);

  const openCopy = (p) => {
    if (!brokerStatus?.linked) {
      toast.error("Link your Alpaca account first (Alpaca Broker tab)");
      setMode("broker");
      return;
    }
    setCopyMode("qty"); setCopyPos(p); setCopyQty(p.quantity); setCopyUsd(String(Math.round((p.current_price || p.avg_price || 0) * p.quantity)));
  };
  const copyQtyResolved = () => {
    if (copyMode === "usd" && copyPos) {
      const px = copyPos.current_price || copyPos.avg_price;
      if (!(px > 0) || !(Number(copyUsd) > 0)) return null;
      return Number((Number(copyUsd) / px).toFixed(6));
    }
    return Number(copyQty);
  };

  const sendToBroker = async () => {
    const qty = copyQtyResolved();
    if (!qty || qty <= 0) { toast.error(copyMode === "usd" ? "Enter a valid $ amount" : "Enter a valid quantity"); return; }
    if (!brokerStatus.paper && !window.confirm(`Place a REAL Market BUY for ${qty} ${copyPos.ticker}? This uses real money in your Alpaca account.`)) return;
    setCopyBusy(true);
    try {
      const r = await api.post("/alpaca/orders", { ticker: copyPos.ticker, side: "buy", quantity: qty, order_type: "market", live_confirmed: !brokerStatus.paper });
      toast.success(`Sent ${copyPos.ticker} to Alpaca · ${r.data.order.status || "submitted"}`);
      setCopyPos(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCopyBusy(false); }
  };

  if (!pf) return <Loader label="Loading portfolio" />;

  return (
    <div>
      <div className="flex items-end justify-between">
        <div><div className="label-xs">Execution Layer</div><h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter">{mode === "paper" ? "Paper Trading" : "Broker · Alpaca"}</h1></div>
        <div className="flex items-center gap-2">
          {mode === "paper" ? (
            <>
              <Button data-testid="add-funds-btn" onClick={() => setShowFunds(true)} className="bg-signal-green hover:bg-signal-green/90 text-black rounded-sm gap-2 h-9"><PlusCircle size={16} weight="fill" /> Add Funds</Button>
              {pf.positions.length > 0 && (
                <Button data-testid="close-all-btn" onClick={closeAll} disabled={busy} variant="outline" className="rounded-sm border-signal-red/40 text-signal-red hover:bg-signal-red/10 gap-2 h-9"><XCircle size={16} weight="fill" /> Close All</Button>
              )}
              <Button data-testid="reset-account-btn" onClick={resetAccount} disabled={busy} variant="outline" className="rounded-sm border-border gap-2 h-9"><ArrowClockwise size={15} /> Reset</Button>
              <DataTag kind="HYPO" />
            </>
          ) : <DataTag kind={brokerStatus?.linked && !brokerStatus.paper ? "REAL" : "HYPO"} />}
        </div>
      </div>

      <div className="mt-4 inline-flex border border-border rounded-sm p-0.5 bg-card" data-testid="trading-mode-toggle">
        <button data-testid="mode-paper" onClick={() => setMode("paper")} className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${mode === "paper" ? "bg-white/10 text-foreground" : "text-muted-foreground"}`}>Paper Simulator</button>
        <button data-testid="mode-broker" onClick={() => setMode("broker")} className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${mode === "broker" ? "bg-white/10 text-foreground" : "text-muted-foreground"}`}>Alpaca Broker</button>
      </div>

      {mode === "broker" ? (
        <div className="mt-5"><Gate need="elite" compact><AlpacaBroker onStatusChange={setBrokerStatus} /></Gate></div>
      ) : (
      <div className="mt-5">

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Total Equity" value={`$${pf.total_equity.toLocaleString()}`} />
        <Stat label="Total P/L" value={<span className={pf.total_pnl >= 0 ? "pos" : "neg"}>{pf.total_pnl >= 0 ? "+" : ""}${pf.total_pnl.toLocaleString()}</span>} sub={<Change value={pf.total_pnl_pct} />} />
        <Stat label="Cash" value={`$${pf.cash.toLocaleString()}`} />
        <Stat label="Concentration Risk" value={<span className="uppercase">{pf.concentration_risk}</span>} sub={`Largest ${pf.largest_position_pct}%`} valueClass={pf.concentration_risk === "high" ? "text-signal-red" : pf.concentration_risk === "medium" ? "text-signal-yellow" : "text-signal-green"} />
      </div>

      <div className="mt-6 flex items-center gap-3">
        <h2 className="font-heading text-xl font-semibold tracking-tight">Stocks Bought</h2>
        <span className="mono text-xs text-muted-foreground">{pf.positions.length} holding{pf.positions.length === 1 ? "" : "s"}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="stocks-bought">
        {pf.positions.length === 0 ? (
          <span className="mono text-xs text-muted-foreground">No stocks bought yet — search a ticker to place your first paper trade.</span>
        ) : pf.positions.map((p) => {
          const lp = live[p.ticker]?.price ?? p.current_price;
          const pnl = lp * p.quantity - p.cost_basis;
          return (
            <button key={p.ticker} data-testid={`bought-${p.ticker}`} onClick={() => nav(`/stock/${p.ticker}`)}
              className="flex items-center gap-2 border border-border bg-card px-3 py-1.5 rounded-sm hover:border-volt/40 transition-colors duration-200">
              <AssetLogo ticker={p.ticker} size={18} />
              <span className="mono font-medium text-sm">{p.ticker}</span>
              <span className="mono text-[11px] text-muted-foreground">×{p.quantity}</span>
              <span className={`mono text-[11px] ${pnl >= 0 ? "pos" : "neg"}`}>{pnl >= 0 ? "+" : ""}${pnl.toFixed(0)}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Positions */}
        <div className="lg:col-span-2 border border-border bg-card hypo-bg">
          <div className="border-b border-border px-4 py-3 label-xs flex justify-between items-center"><span>Open Positions · Simulated</span><LiveBadge connected={connected} /></div>
          {pf.positions.length === 0 ? <Empty>No open positions — search a ticker to place a paper trade</Empty> : (
            <>
              <div className="grid grid-cols-[1fr_70px_90px_90px_1fr] gap-2 px-4 py-2 label-xs border-b border-border/50">
                <span>Symbol</span><span className="text-right">Qty</span><span className="text-right">Avg</span><span className="text-right">Last</span><span className="text-right">Unreal. P/L</span>
              </div>
              {pf.positions.map((p) => {
                const lp = live[p.ticker]?.price ?? p.current_price;
                const mv = lp * p.quantity;
                const pnl = mv - p.cost_basis;
                const pnlPct = p.cost_basis ? (pnl / p.cost_basis) * 100 : 0;
                return (
                <div key={p.ticker} className="grid grid-cols-[1fr_70px_90px_90px_1fr] gap-2 items-center px-4 py-3 border-b border-border/50 hover:bg-white/[0.03] transition-colors duration-200">
                  <button onClick={() => nav(`/stock/${p.ticker}`)} className="flex items-center gap-2 text-left"><AssetLogo ticker={p.ticker} size={24} /><div><div className="mono font-medium">{p.ticker}</div><div className="text-xs text-muted-foreground">{p.sector}</div></div></button>
                  <div className="mono text-right text-sm">{p.quantity}</div>
                  <div className="mono text-right text-sm">${p.avg_price}</div>
                  <div className="text-right text-sm"><LivePrice value={Number(lp.toFixed(2))} /></div>
                  <div className="flex items-center justify-end gap-3">
                    <div className="text-right"><div className={`mono text-sm ${pnl >= 0 ? "pos" : "neg"}`}>{pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}</div><div className="mono text-[11px]"><Change value={pnlPct} /></div></div>
                    {has("elite") && (
                      <Button data-testid={`copy-broker-${p.ticker}`} size="sm" variant="outline" onClick={() => openCopy(p)} className="h-7 rounded-sm border-volt/40 text-volt hover:bg-volt/10 text-xs gap-1"><Bank size={13} weight="fill" /> Broker</Button>
                    )}
                    <Button data-testid={`close-${p.ticker}`} size="sm" variant="outline" onClick={() => close(p.ticker)} className="h-7 rounded-sm border-border text-xs">Close</Button>
                  </div>
                </div>
                );
              })}
            </>
          )}
        </div>

        {/* Allocation */}
        <div className="border border-border bg-card p-5">
          <div className="label-xs mb-3">Sector Allocation</div>
          {pf.sector_allocation.length === 0 ? <Empty>No allocation yet</Empty> : (
            <>
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pf.sector_allocation} dataKey="value" nameKey="sector" innerRadius={45} outerRadius={75} strokeWidth={0}>
                      {pf.sector_allocation.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#141415", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 space-y-1.5">
                {pf.sector_allocation.map((s, i) => (
                  <div key={s.sector} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />{s.sector}</span>
                    <span className="mono">{s.pct}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Trade ledger */}
      <div className="mt-4 border border-border bg-card">
        <div className="border-b border-border px-4 py-3 label-xs">Trade Ledger</div>
        {trades.length === 0 ? <Empty>No trades executed yet</Empty> : trades.slice(0, 20).map((t, i) => (
          <div key={i} className="grid grid-cols-[1fr_80px_80px_120px_1fr] gap-2 items-center px-4 py-2.5 border-b border-border/50 text-sm">
            <span className="mono font-medium">{t.ticker}</span>
            <span className={`mono uppercase text-xs ${t.side === "buy" ? "pos" : "neg"}`}>{t.side}</span>
            <span className="mono text-right">{t.quantity}</span>
            <span className="mono text-right">${t.price}</span>
            <span className="mono text-right text-xs text-muted-foreground">{new Date(t.timestamp).toLocaleString()}</span>
          </div>
        ))}
      </div>
      </div>
      )}

      {/* Copy-to-broker dialog */}
      <Dialog open={!!copyPos} onOpenChange={(o) => !o && setCopyPos(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="copy-broker-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Bank size={18} weight="fill" className="text-volt" /> Send to Alpaca</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Copy this paper holding as a Market BUY to your linked Alpaca account.</DialogDescription>
          </DialogHeader>
          {copyPos && (
            <div className="space-y-4">
              <div className={`border p-3 rounded-sm ${brokerStatus?.paper ? "border-signal-green/30 bg-signal-green/5" : "border-signal-red/40 bg-signal-red/5"}`}>
                <div className="label-xs">Routing to Alpaca</div>
                <div className={`mono text-sm mt-1 ${brokerStatus?.paper ? "text-signal-green" : "text-signal-red"}`}>{brokerStatus?.paper ? "PAPER account (no real money)" : "LIVE account · REAL MONEY"}</div>
              </div>
              <div className="hypo-bg border border-white/10 p-3 rounded-sm">
                <div className="label-xs italic">Copying paper position</div>
                <div className="mono text-lg mt-1">{copyPos.ticker} <span className="text-muted-foreground text-sm">· Market BUY</span></div>
                <div className="mono text-[11px] text-muted-foreground mt-1">Paper holding: {copyPos.quantity} @ ${copyPos.avg_price}</div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="label-xs">{copyMode === "usd" ? "$ Amount to buy on Alpaca" : "Quantity to buy on Alpaca"}</div>
                  <div className="flex rounded-sm overflow-hidden border border-border h-5" data-testid="copy-broker-amt-toggle">
                    {["qty", "usd"].map((m) => (
                      <button key={m} data-testid={`copy-broker-amt-${m}`} onClick={() => setCopyMode(m)}
                        className={`mono text-[9px] uppercase px-1.5 ${copyMode === m ? "bg-volt/20 text-volt" : "text-muted-foreground"}`}>{m === "usd" ? "$" : "Qty"}</button>
                    ))}
                  </div>
                </div>
                {copyMode === "usd" ? (
                  <>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 mono text-sm text-muted-foreground">$</span>
                      <Input data-testid="copy-broker-usd" type="number" min="1" step="any" value={copyUsd} onChange={(e) => setCopyUsd(e.target.value)} className="bg-background rounded-sm mono pl-6" />
                    </div>
                    <div className="mono text-[10px] text-muted-foreground mt-1" data-testid="copy-broker-estimate">≈ {copyQtyResolved() ? copyQtyResolved().toLocaleString(undefined, { maximumFractionDigits: 6 }) : "—"} {copyPos.ticker.includes("-USD") ? "coins" : "shares"} @ ~${copyPos.current_price || copyPos.avg_price}</div>
                    <div className="mt-2"><AmountChips onPick={(v) => setCopyUsd(String(v))} testid="copy-broker-chips" /></div>
                  </>
                ) : (
                  <Input data-testid="copy-broker-qty" type="number" min="0.0001" step="any" value={copyQty} onChange={(e) => setCopyQty(e.target.value)} className="bg-background rounded-sm mono" />
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyPos(null)} className="rounded-sm border-border">Cancel</Button>
            <Button data-testid="copy-broker-confirm" disabled={copyBusy} onClick={sendToBroker} className={`rounded-sm ${brokerStatus?.paper ? "bg-signal-green hover:bg-signal-green/90 text-black" : "bg-signal-red hover:bg-signal-red/90 text-white"}`}>{copyBusy ? "Sending…" : brokerStatus?.paper ? "Send to Alpaca" : "Send LIVE Order"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Funds dialog */}
      <Dialog open={showFunds} onOpenChange={setShowFunds}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="add-funds-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><PlusCircle size={18} weight="fill" className="text-signal-green" /> Add Paper Funds</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Add simulated cash to your paper trading account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="hypo-bg border border-white/10 p-3 rounded-sm">
              <div className="label-xs italic">Current cash</div>
              <div className="mono text-xl mt-1">${pf.cash.toLocaleString()}</div>
            </div>
            <div>
              <div className="label-xs mb-2">Quick add</div>
              <div className="grid grid-cols-3 gap-2">
                {[10000, 50000, 100000].map((v) => (
                  <Button key={v} data-testid={`quick-${v}`} variant="outline" disabled={busy} onClick={() => deposit(v)} className="rounded-sm border-border mono">+${v / 1000}k</Button>
                ))}
              </div>
            </div>
            <div>
              <div className="label-xs mb-2">Custom amount</div>
              <Input data-testid="funds-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-background rounded-sm mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFunds(false)} className="rounded-sm border-border">Cancel</Button>
            <Button data-testid="confirm-funds-btn" disabled={busy} onClick={() => deposit(amount)} className="bg-signal-green hover:bg-signal-green/90 text-black rounded-sm">{busy ? "Adding…" : "Add Funds"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
